import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';

function BlogEmotionalNeglect() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-warm-50">
      <Navigation variant="soft-brown" scrolled={true} />
      
      {/* Article Header */}
      <section className="px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-sage-600 hover:text-sage-800 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6">
            The Invisible Wound: Understanding Emotional Neglect
          </h1>
          
          <div className="flex items-center text-gray-600 text-sm mb-8">
            <span>By Yasi Philipos</span>
            <span className="mx-3">•</span>
            <span>8 min read</span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Emotional neglect is one of the most invisible forms of trauma because it's defined by what <em>didn't</em> happen rather than what did. Your physical needs were met. You had food, shelter, and safety. But your emotional needs... to be seen, heard, validated, comforted... were consistently unmet or dismissed. You learned early that your feelings were inconvenient, your needs were too much, and your worth depended on being easy, helpful, and not asking for anything.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              <strong className="text-sage-800">This isn't about blaming your parents or caregivers.</strong> Most often, they were doing the best they could with the tools and awareness they had at the time. They were likely dealing with their own unhealed trauma, their own people-pleasing patterns. These patterns are passed down through generations—not because anyone wanted to hurt you, but because hurt people often unintentionally pass on what was never healed in them.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              <strong className="text-sage-800">People-pleasing was a brilliant coping mechanism.</strong> When emotional attunement was missing, you adapted by becoming hyper-attuned to everyone else. You learned to read the room, anticipate needs, adjust your behavior, make yourself useful. It kept you safe. It earned you approval. It made you feel less alone. And it worked... until it didn't.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              The problem is that this coping mechanism is now outdated. <strong className="text-sage-800">You don't have to continue living with a survival strategy built for a childhood you no longer live in.</strong> Your nervous system is still running an old program designed to keep you safe in an environment that no longer exists. The good news? It can be updated. You can teach your body that it's safe to have needs, safe to take up space, safe to say no.
            </p>
          </div>

          {/* CTA at the bottom */}
          <div className="mt-12 bg-gradient-to-br from-sage-50 to-warm-50 rounded-3xl p-8 md:p-12 text-center border border-sage-100">
            <h3 className="text-2xl md:text-3xl font-serif font-light text-sage-800 mb-4">
              Ready to update that old program?
            </h3>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Book a free 15-minute consultation to see if we're a good fit. No pressure, no obligation—just a conversation about how I can support you.
            </p>
            <Link to="/#booking" className="inline-block bg-warm-500 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-warm-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
              Book Free Consultation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default BlogEmotionalNeglect;
